import { useEffect, useState } from 'react'
import { Shield, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useReviewPanel } from '@/contexts/ReviewPanelContext'
import { capture } from '@/lib/analytics'
import { TriageCard } from './TriageCard'
import type { StatusKey } from './statusConfig'

export function TriageView() {
  const {
    apiAuditResult,
    hasAuditResults,
    isAuditing,
    progressMessage,
    auditError,
    focusedClaimIndices,
    activeBreakdownIndices,
    onRunAudit,
    claimSnapshots,
    removedCitations,
    restoreCitation,
    setSurgeryClaimId,
    setViewMode,
  } = useReviewPanel()

  // Collapsible state for the "Removed citations" group at the bottom of the
  // triage list. Default closed — the writer's main focus is active claims;
  // the group is just for restore/recovery.
  const [removedGroupOpen, setRemovedGroupOpen] = useState(false)

  const enterSurgery = (claimId: string) => {
    capture('surgery_entered')
    setSurgeryClaimId(claimId)
    setViewMode('surgery')
  }

  // Auto-scroll to focused card when citation is clicked in editor
  useEffect(() => {
    if (!focusedClaimIndices?.length) return
    const el = document.getElementById(`triage-card-${focusedClaimIndices[0]}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [focusedClaimIndices])

  // ── Empty / loading / error states ──
  if (!hasAuditResults) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        {isAuditing ? (
          <>
            <Loader2 className="h-10 w-10 text-pharma-primary mb-3 animate-spin" />
            <h3 className="text-sm font-medium text-foreground mb-1">Auditing…</h3>
            {progressMessage && (
              <p className="text-xs text-muted-foreground mt-1 max-w-[220px]">
                {progressMessage}
              </p>
            )}
          </>
        ) : auditError ? (
          <>
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 mb-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-sm font-medium text-foreground mb-1">Audit Failed</h3>
            <p className="text-xs text-red-600 mb-4 max-w-[220px]">{auditError}</p>
            <Button
              onClick={onRunAudit}
              className="bg-pharma-primary hover:bg-pharma-primary/90 text-white gap-1.5"
            >
              Retry Audit
            </Button>
          </>
        ) : (
          <>
            <Shield className="h-10 w-10 text-muted-foreground mb-3" />
            <h3 className="text-sm font-medium text-foreground mb-1">
              Ready to Audit
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Write your claims in the editor, upload a reference document to your library, then run a compliance audit to verify each claim against your sources.
            </p>
            <Button
              onClick={onRunAudit}
              className="bg-pharma-primary hover:bg-pharma-primary/90 text-white gap-1.5"
            >
              Run Compliance Audit
            </Button>
          </>
        )}
      </div>
    )
  }

  // ── No API result yet (legacy EvidenceLocker handles this in RightPanel) ──
  if (!apiAuditResult) return null

  const entries = apiAuditResult.atomic_breakdown
  const claimCount = entries.length

  // D12: split entries — D4-removed claims fold into a collapsible group at
  // the bottom so the main triage flow stays clean. Active claims (citation
  // still in the doc) render as today.
  type Indexed = { entry: (typeof entries)[number]; i: number }
  const activeEntries: Indexed[] = []
  const removedEntries: Indexed[] = []
  for (let i = 0; i < entries.length; i++) {
    const claimId = String(i)
    const snap = claimSnapshots.get(claimId)
    const isRemovedViaD4 = !!(snap?.claimUuid && removedCitations.has(snap.claimUuid))
    if (isRemovedViaD4) removedEntries.push({ entry: entries[i], i })
    else activeEntries.push({ entry: entries[i], i })
  }

  return (
    <ScrollArea className="flex-1 min-h-0">
      <div className="bg-muted p-3 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="font-headline text-sm font-bold tracking-wide uppercase text-muted-foreground">
            Review Pipeline
          </h3>
          <Badge variant="secondary" className="text-xs">
            {claimCount} claim{claimCount !== 1 ? 's' : ''}
          </Badge>
        </div>

        {/* Active triage cards */}
        {activeEntries.map(({ entry, i }) => {
          const isActive = !activeBreakdownIndices || activeBreakdownIndices.has(i)
          const isSelected = (focusedClaimIndices ?? []).includes(i)
          const claimId = String(i)
          const snap = claimSnapshots.get(claimId)
          const effectiveStatus = (snap?.effectiveStatus ?? entry.status) as StatusKey
          const isRemoved = !!(snap?.claimUuid && removedCitations.has(snap.claimUuid))
          return (
            <div key={i} className={!isActive ? 'opacity-40' : ''}>
              {!isActive && (
                <p className="text-xs text-muted-foreground italic mb-1 px-1">Removed from document</p>
              )}
              <TriageCard
                entry={entry}
                claimIndex={i}
                isSelected={isSelected}
                effectiveStatus={effectiveStatus}
                isRemoved={isRemoved}
                onSelect={() => enterSurgery(claimId)}
                onRestore={snap?.claimUuid ? () => { restoreCitation(snap.claimUuid!) } : undefined}
              />
            </div>
          )
        })}

        {/* Removed citations group (D4 — collapsible, default closed) */}
        {removedEntries.length > 0 && (
          <div className="-mt-2">
            <button
              type="button"
              className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider cursor-pointer flex items-center gap-1.5 py-1.5 w-full hover:text-foreground transition-colors"
              onClick={() => setRemovedGroupOpen((o) => !o)}
            >
              <span className={`text-[10px] transition-transform duration-200 ${removedGroupOpen ? 'rotate-90' : ''}`}>
                ›
              </span>
              Removed citations ({removedEntries.length})
            </button>
            {removedGroupOpen && (
              <div className="flex flex-col gap-2.5 pt-1">
                {removedEntries.map(({ entry, i }) => {
                  const isSelected = (focusedClaimIndices ?? []).includes(i)
                  const claimId = String(i)
                  const snap = claimSnapshots.get(claimId)
                  const effectiveStatus = (snap?.effectiveStatus ?? entry.status) as StatusKey
                  const isRemoved = !!(snap?.claimUuid && removedCitations.has(snap.claimUuid))
                  return (
                    <TriageCard
                      key={i}
                      entry={entry}
                      claimIndex={i}
                      isSelected={isSelected}
                      effectiveStatus={effectiveStatus}
                      isRemoved={isRemoved}
                      onSelect={() => enterSurgery(claimId)}
                      onRestore={snap?.claimUuid ? () => { restoreCitation(snap.claimUuid!) } : undefined}
                    />
                  )
                })}
              </div>
            )}
          </div>
        )}

        <div className="pb-2" />
      </div>
    </ScrollArea>
  )
}
